import { academyPath } from '../schema/paths';

/**
 * Absolute origin of the Academy API. NOT for browser data calls (use academyApiUrl() so
 * the session cookie is sent) — this is only for the top-level auth navigations
 * (getSamlLoginUrl(), getContentstackAuthorizeUrl() in lib/auth/idp-urls.ts) whose targets
 * are registered with the IdP and must stay absolute.
 */
export function getApiBaseUrl(): string {
  return process.env.NEXT_PUBLIC_BASE_URL ?? '';
}

/**
 * URL for a browser XHR to the Academy API — always relative, so it stays same-origin and
 * `connect.sid` actually gets sent (a direct call to the API's own host wouldn't carry it).
 * Rides the /academy/v1/* rewrite in next.config.ts. The /academy prefix is required, not
 * cosmetic: it is the only form that rewrite matches, and in production a bare /v1/* would
 * hit the marketing site instead of this app.
 *
 * BROWSER ONLY. Server code uses academyApiFetch() (lib/auth/common/academy-api.ts).
 */
export function academyApiUrl(path: string): string {
  return academyPath(path);
}

/**
 * Absolute origin of the Academy API for SERVER-side calls only — Node's fetch can't resolve
 * a relative URL (browser calls use academyApiUrl() instead).
 */
export function getServerApiOrigin(): string {
  return process.env.NEXT_PUBLIC_BASE_URL ?? 'http://localhost:3001';
}

/** CSRF marker headers the Academy API requires on every call; spread by academyApiHeaders(), utils/axios-handler.ts, and lib/auth/common/academy-api.ts. */
export const ACADEMY_CSRF_HEADERS = {
  'X-Requested-With': 'XMLHttpRequest',
  'X-CSRF-Check': 'true',
} as const;

/**
 * Headers every direct browser `fetch` to the Academy API needs (certificate-api,
 * assessment-api): the CSRF marker. Axios call sites don't need this —
 * utils/axios-handler.ts adds ACADEMY_CSRF_HEADERS itself.
 */
export function academyApiHeaders(extra?: Record<string, string>): Record<string, string> {
  return {
    ...ACADEMY_CSRF_HEADERS,
    ...extra,
  };
}

// getSamlLoginUrl() and getContentstackAuthorizeUrl() live in lib/auth/idp-urls.ts

/** App-relative base path for this app's own auth API routes (same-origin, so cookies attach). */
export function getAuthApiBasePath(): string {
  return academyPath('/api/auth');
}
